import { Button, Card, CardTitle, IconButton, Link, Select, Spinner } from '@1k5-tech-es/grado-react';
import { ChevronLeft, ChevronRight, Mail } from 'lucide-react';
import { useState } from 'react';

import type { EmailAdvisor, QuarterSummary, ReportableQuarter } from '../../api/types';
import { formatDateTime } from '../../lib/format';
import { describeEmailError, STATUS_LABEL } from './emails';

/** One advisor's PDF rendered inline, with quick navigation through the whole quarter. */
export function AdvisorPreview({
  quarter,
  summary,
  chosenId,
  onChoose,
  emailInfo,
  onSendEmail,
}: {
  quarter: ReportableQuarter;
  summary: QuarterSummary | undefined;
  chosenId: number | null;
  onChoose: (advisorId: number) => void;
  /** null when sending by email is off. */
  emailInfo: (advisorId: number) => EmailAdvisor | null;
  onSendEmail: (advisorId: number) => void;
}) {
  const advisors = summary?.advisors ?? [];
  const setChosenId = onChoose;
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);

  // Keep the chosen advisor while they have data in the quarter; otherwise the first one.
  const index = Math.max(0, advisors.findIndex((a) => a.id === chosenId));
  const advisor = advisors[index];
  const src = advisor
    ? `/api/reports/preview?year=${quarter.end.year}&month=${quarter.end.month}&advisorId=${advisor.id}`
    : null;
  const loading = src !== null && loadedSrc !== src;
  const go = (delta: number) => setChosenId(advisors[(index + delta + advisors.length) % advisors.length]!.id);

  return (
    <Card id="vista-previa">
      <div className="border-b border-border p-4">
        <div className="flex items-center justify-between gap-3">
          <CardTitle as="h2">Vista previa</CardTitle>
          {src && (
            <Link href={src} external className="text-xs font-semibold">
              Abrir en pestaña nueva
            </Link>
          )}
        </div>
        <div className="mt-3 flex gap-2">
          <IconButton variant="secondary" icon={ChevronLeft} label="Asesor anterior" disabled={advisors.length < 2} onClick={() => go(-1)} />
          <Select
            className="min-w-0 flex-1"
            value={advisor?.id ?? ''}
            disabled={advisors.length === 0}
            onChange={(e) => setChosenId(Number(e.target.value))}
            aria-label="Asesor"
          >
            {advisors.map((a) => (
              <option key={a.id} value={a.id}>
                {a.fullName}
                {a.teamCode ? ` · ${a.teamCode}` : ''}
              </option>
            ))}
          </Select>
          <IconButton variant="secondary" icon={ChevronRight} label="Asesor siguiente" disabled={advisors.length < 2} onClick={() => go(1)} />
        </div>
        {advisor && (
          <p className="mt-2 text-xs text-muted-foreground">
            {index + 1} de {advisors.length} · el PDF se genera al momento y no se guarda
          </p>
        )}
        {advisor && <EmailLine info={emailInfo(advisor.id)} onSend={() => onSendEmail(advisor.id)} />}
      </div>

      <div className="relative aspect-[210/297] bg-muted">
        {src ? (
          <iframe
            key={src}
            src={`${src}#view=Fit`}
            title={`Informe de ${advisor!.fullName}`}
            className="absolute inset-0 size-full"
            onLoad={() => setLoadedSrc(src)}
          />
        ) : (
          <div className="absolute inset-0 grid place-items-center text-sm text-muted-foreground">
            {summary ? 'No hay asesores con datos en este trimestre.' : <Spinner />}
          </div>
        )}
        {loading && (
          <div className="absolute inset-0 grid place-items-center bg-muted/80 text-sm text-muted-foreground">
            <span className="flex items-center gap-2">
              <Spinner size="sm" decorative /> Generando el informe de {advisor!.fullName}…
            </span>
          </div>
        )}
      </div>
    </Card>
  );
}

/** The advisor's address, how their report by email went, and the button to send it. */
function EmailLine({ info, onSend }: { info: EmailAdvisor | null; onSend: () => void }) {
  if (!info) return null;
  const last = info.lastEmail;
  const busy = last?.status === 'queued' || last?.status === 'sending';
  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-muted px-3 py-2">
      <div className="min-w-0 text-xs">
        {info.email ? (
          <span className="break-all text-muted-foreground">{info.email}</span>
        ) : (
          <span className="text-warning-text">Sin email: añádelo en Asesores</span>
        )}
        {last && (
          <span className={`block ${last.status === 'failed' ? 'text-destructive-text' : 'text-muted-foreground'}`}>
            {STATUS_LABEL[last.status]}
            {last.status === 'sent' && last.sentAt && ` el ${formatDateTime(last.sentAt)}`}
            {last.status === 'failed' && ` · ${describeEmailError(last.errorCode)}`}
          </span>
        )}
      </div>
      <Button variant="secondary" size="sm" icon={Mail} className="shrink-0" disabled={!info.email || busy} onClick={onSend}>
        {busy ? 'En cola…' : last?.status === 'sent' ? 'Reenviar por correo' : 'Enviar por correo'}
      </Button>
    </div>
  );
}
